import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { CameraIcon, PaletteIcon, PersonStandingIcon, SunIcon, WrenchIcon } from 'lucide-react';

type ToolCollapsedSummaryProps = {
  data: Record<string, any> | undefined;
  className?: string;
};

const getToolMeta = (toolId: string | undefined) => {
  switch (toolId) {
    case 'relight':
      return { nameKey: 'tool.relight.name', Icon: SunIcon };
    case 'pose':
      return { nameKey: 'tool.pose.name', Icon: PersonStandingIcon };
    case 'camera':
      return { nameKey: 'tool.camera.name', Icon: CameraIcon };
    case 'lut':
      return { nameKey: 'tool.lut.name', Icon: PaletteIcon };
    default:
      return { nameKey: undefined, Icon: WrenchIcon };
  }
};

export const ToolCollapsedSummary = ({ data, className }: ToolCollapsedSummaryProps) => {
  const { t } = useI18n();
  const d = (data ?? {}) as any;
  const toolId = typeof d?.tool?.id === 'string' && d.tool.id ? String(d.tool.id) : undefined;
  const lastRunAt = typeof d?.tool?.last_run_at === 'string' && d.tool.last_run_at ? String(d.tool.last_run_at) : undefined;

  const { nameKey, Icon } = getToolMeta(toolId);
  const toolName = toolId ? (nameKey ? t(nameKey) : toolId) : t('Waiting for selection');

  const lastRunText = (() => {
    if (!lastRunAt) return undefined;
    try {
      const dt = new Date(lastRunAt);
      if (Number.isNaN(dt.getTime())) return lastRunAt;
      return dt.toLocaleString();
    } catch {
      return lastRunAt;
    }
  })();

  return (
    <div className={cn('flex h-full w-full items-start gap-4 p-3', className)}>
      <div className="h-32 w-32 shrink-0 overflow-hidden rounded-2xl border border-sidebar-border/30 bg-sidebar-accent/30 flex items-center justify-center shadow-inner">
        <Icon size={32} className="text-muted-foreground/40" />
      </div>

      <div className="min-w-0 flex-1 flex flex-col h-32">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/30 mb-1">{t('Tool')}</div>
        <div className="text-sm font-bold truncate text-foreground/90">{toolName}</div>
        <div className="mt-2 flex flex-1 flex-col text-[11px] leading-snug">
          {lastRunText ? (
            <div className="truncate">
              <span className="text-muted-foreground/60">{t('Updated')}:</span>{' '}
              <span className="text-foreground/80 font-medium">{lastRunText}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};


